import type { LucideIcon } from 'lucide-react';
import type { ExperimentRecommendation } from 'statisticsdb/types';
import { CircleCheck, CircleSlash, TriangleAlert } from 'lucide-react';
import { cn } from 'helpers/cn';

type RecommendationBannerProps = {
  recommendation: ExperimentRecommendation | null;
};

/**
 * The single most important sentence on the page, so it has to out-read the tables
 * under it rather than sit quieter than them.
 *
 * Every level is filled, in its own colour. It used to fill only the low-confidence
 * and insufficient states, because `--dangerBg` was the only wash the design sheet
 * shipped — so the same banner looked like two different components depending on
 * its verdict, and the loudest treatment landed on the verdict that asks for the
 * least. `--okBg` and `--warnBg` exist now for exactly this.
 *
 * Weight also comes from the sentence being set as a headline (15px Roboto 500, not
 * 13px supporting copy), a 3px left edge, and an icon sized to match.
 *
 * The outcome carries the colour: ok where the result is actionable, warn where it
 * is directional, danger where there is nothing to act on. Colour is never the only
 * signal — each state also carries its own worded label and its own icon.
 */
type ConfidenceStyle = { edge: string; chip: string; tone: string; fill: string; Icon: LucideIcon };

const confidenceStyles: Record<string, ConfidenceStyle> = {
  high: {
    edge: 'border-l-ok',
    chip: 'border-ok-line text-ok',
    tone: 'text-ok',
    fill: 'bg-ok-bg',
    Icon: CircleCheck,
  },
  medium: {
    edge: 'border-l-warn',
    chip: 'border-warn-line text-warn',
    tone: 'text-warn',
    fill: 'bg-warn-bg',
    Icon: TriangleAlert,
  },
  low: {
    edge: 'border-l-danger',
    chip: 'border-danger-line text-danger',
    tone: 'text-danger',
    fill: 'bg-danger-bg',
    Icon: CircleSlash,
  },
  insufficient: {
    edge: 'border-l-danger',
    chip: 'border-danger-line text-danger',
    tone: 'text-danger',
    fill: 'bg-danger-bg',
    Icon: CircleSlash,
  },
};

const confidenceLabels: Record<string, string> = {
  high: 'High Confidence',
  medium: 'Medium Confidence',
  low: 'Low Confidence',
  insufficient: 'Insufficient Data',
};

export const RecommendationBanner = ({ recommendation }: RecommendationBannerProps) => {
  if (!recommendation) return null;

  const styles = confidenceStyles[recommendation.confidence] ?? confidenceStyles.insufficient!;
  const { Icon } = styles;

  return (
    <div
      className={cn(
        // border-l-[3px] over the structural hairline: the left edge is the loudest
        // thing the palette can give this without inventing a fill.
        'border-line rounded-card flex items-start gap-3 border border-l-[3px] px-4 py-3.5',
        styles.fill,
        styles.edge,
      )}
      data-tour="results-verdict"
    >
      <Icon className={cn('mt-0.5 size-[18px] shrink-0', styles.tone)} strokeWidth={1.75} aria-hidden="true" />

      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="micro-label">Recommendation</span>
          <span
            className={cn(
              'rounded-pill border px-2 py-0.5 font-mono text-nano uppercase tracking-[0.1em]',
              styles.chip,
            )}
          >
            {confidenceLabels[recommendation.confidence]}
          </span>
        </div>

        {/* A headline, not supporting copy. */}
        <p className="text-foreground m-0 text-title font-medium leading-snug">{recommendation.message}</p>
      </div>
    </div>
  );
};
